import { MaterialPageSectionsNames } from '../../../constants/material-page-sections';
import { MaterialType, PreviewsType } from '../../../types/material-page';
import { Reaction } from '../../../types/reaction';
import { CatalogProps } from '../../../utils/enhanced-ecommerce';
import qualityEventsHelpers, { stringifyExtra } from '../quality-events.helpers';

export enum MATERIAL_PREVIEW_SLIDE_DIRECTION {
    LEFT = 'Left',
    RIGHT = 'Right'
}

// AB TEST - 1406 (remove on reject and keep on accept)
const URGENCY_SIGNAL_TYPE = 'purchases_last_30_days';

// AB TEST - 1406 (remove on reject and keep on accept)
export type ViewMaterialUrgencySignal = { shown: boolean; value?: number };

type MaterialClickType = {
    material: Material;
    props: CatalogProps;
    source?: string;
    variant?: string;
    extra?: Record<string, string | number>;
    searchRequestId?: string;
};

export const MATERIAL_PAGE_EVENTS = {
    preview: {
        favShareSchoolAbtest: {
            schoolBtnClick() {
                qualityEventsHelpers.track({
                    type: 'FSAP_school-wishlist-click'
                });
            },
            shareBtnClick() {
                qualityEventsHelpers.track({
                    type: 'FSAP_share-click'
                });
            },
            favouriteBtnClick() {
                qualityEventsHelpers.track({
                    type: 'FSAP_favourite-click'
                });
            }
        },
        showMaterialPreview(materialInfo: MaterialType, extra?: string) {
            qualityEventsHelpers.track({
                ...qualityEventsHelpers.extractMaterialsData(materialInfo),
                type: 'showMaterialPreview',
                extra
            });
        },
        zoomMaterialPreview() {
            qualityEventsHelpers.track({
                type: 'zoom-material-preview'
            });
        },
        selectThumbnail(materialInfo: MaterialType) {
            return {
                ...qualityEventsHelpers.extractMaterialsData(materialInfo),
                type: 'select-material-thumbnail'
            };
        },
        galleryCounterClick() {
            const extra = JSON.stringify({ isMobile: window.innerWidth < 991 });

            qualityEventsHelpers.track({
                type: 'gallery-counter-click',
                extra
            });
        },
        slideMaterialGalleryPreviews(isOtherInfoSection: boolean) {
            return {
                type: isOtherInfoSection
                    ? 'other-section-slide-material-gallery-previews'
                    : 'slide-material-gallery-previews'
            };
        },
        slideMaterialGalleryPreviewsByArrowClick(direction: MATERIAL_PREVIEW_SLIDE_DIRECTION) {
            qualityEventsHelpers.track({
                type: 'slide-material-gallery-previews-by-arrow-click',
                extra: direction
            });
        },
        slideMaterialGalleryPreviewsByEmptySpaceClick(direction: MATERIAL_PREVIEW_SLIDE_DIRECTION) {
            qualityEventsHelpers.track({
                type: 'slide-material-gallery-previews-by-empty-space-click',
                extra: direction
            });
        },
        slide(materialInfo: MaterialType, direction: MATERIAL_PREVIEW_SLIDE_DIRECTION, source?: string) {
            return {
                ...qualityEventsHelpers.extractMaterialsData(materialInfo),
                source,
                type: 'slide-small-material-preview',
                extra: direction
            };
        },
        closeMaterialPreview(method: string) {
            qualityEventsHelpers.track({
                type: 'close-material-preview',
                extra: method
            });
        },
        clickOnGalleryMainImage() {
            qualityEventsHelpers.track({
                type: 'click-gallery-main-image'
            });
        },
        previewTap() {
            qualityEventsHelpers.track({
                type: 'mp_ImageTap'
            });
        },
        swipeMaterialPreviews(direction: 'left' | 'right', previewIndex: number) {
            const extra = JSON.stringify({ direction, previewIndex });

            return {
                type: 'swipe-mobile-material-preview',
                extra
            };
        },
        previewRightClick(materialId: number) {
            qualityEventsHelpers.track({
                type: 'preview-right-click',
                extra: JSON.stringify(materialId)
            });
        },
        pinchZoomMaterialPreview(materialId: number, zoomedPreview: PreviewsType) {
            qualityEventsHelpers.track({
                type: 'pinch-zoom-material-preview',
                extra: JSON.stringify({ materialId, zoomedPreview })
            });
        },
        onThumbnailClick({ materialInfo, place = '' }: { materialInfo?: MaterialType; place?: string }) {
            const extraData = materialInfo ? qualityEventsHelpers.extractMaterialsData(materialInfo) : {};

            return {
                ...extraData,
                extra: place,
                type: 'click-material-thumbnail'
            };
        },
        openGalleryByIconClick() {
            qualityEventsHelpers.track({
                type: 'open-gallery-by-icon-click'
            });
        }
    },
    mobileGallery: {
        expandIconClick(materialId: number, previewIndex: number, showMobileGallery: boolean) {
            const extra = JSON.stringify({ materialId, previewIndex, showMobileGallery });

            qualityEventsHelpers.track({
                type: 'expand-icon-click',
                extra
            });
        },
        previewTap(materialId: number, previewIndex: number) {
            const extra = JSON.stringify({ materialId, previewIndex });

            qualityEventsHelpers.track({
                type: 'mobile-preview-tap',
                extra
            });
        },
        closeCarouselGallery(materialId: number, lastSeenPreview: number) {
            const extra = JSON.stringify({ materialId, lastSeenPreview });

            qualityEventsHelpers.track({
                type: 'close-carousel-gallery',
                extra
            });
        },
        openCarouselGallery(materialId: number) {
            const extra = JSON.stringify({ materialId });

            qualityEventsHelpers.track({
                type: 'open-carousel-gallery',
                extra
            });
        },
        swipeCarouselPreviews(materialId: number, direction: 'left' | 'right', previewIndex: number) {
            const extra = JSON.stringify({ materialId, direction, previewIndex });

            return {
                type: 'swipe-carousel-previews',
                extra
            };
        },
        pinchZoomPreview(materialId: number, previewIndex: number) {
            const extra = JSON.stringify({ materialId, previewIndex });

            qualityEventsHelpers.track({
                type: 'pinch-zoom-mobile-preview',
                extra
            });
        }
    },
    priceBlock: {
        addToCart(
            material: Material,
            materialItem: string | null,
            materialIndex?: number,
            from?: string,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            extraProp?: any,
            searchRequestId?: string
        ) {
            qualityEventsHelpers.track({
                ...qualityEventsHelpers.extractMaterialsData(material, { itemIndex: materialIndex }),
                extra: stringifyExtra({
                    referrer: materialItem,
                    from,
                    ...extraProp,
                    search_request_id: searchRequestId
                }),
                type: 'addToCart'
            });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        addToCartInactive(extra?: any) {
            qualityEventsHelpers.track({
                type: 'addToCart-inactive-redirect',
                extra: JSON.stringify(extra)
            });
        },
        buyNow(material: Material) {
            qualityEventsHelpers.track({
                ...qualityEventsHelpers.extractMaterialsData(material),
                type: 'addToCart',
                extra: 'buyNow'
            });
        }
    },
    showCopySharePopup() {
        qualityEventsHelpers.track({
            type: 'mp_ShareMaterial'
        });
    },
    socialShareBtn(extra: string) {
        qualityEventsHelpers.track({
            type: 'SocialShareButtonClick',
            extra
        });
    },
    materialClick({ material, props, source, variant, extra, searchRequestId }: MaterialClickType) {
        qualityEventsHelpers.track({
            ...qualityEventsHelpers.extractMaterialsData(material, props),
            internal_path: props.internalPath,
            source,
            type: 'click',
            test_segment: variant,
            extra: stringifyExtra({ ...extra, search_request_id: searchRequestId })
        });
    },
    viewMaterial(materialData: Material, materialItem: string | null, urgencySignal?: ViewMaterialUrgencySignal) {
        const urgencySignalExtra = urgencySignal && {
            urgency_signal_shown: urgencySignal.shown,
            ...(urgencySignal.shown && {
                urgency_signal_type: URGENCY_SIGNAL_TYPE,
                urgency_signal_value: urgencySignal.value
            })
        };

        qualityEventsHelpers.track({
            ...qualityEventsHelpers.extractMaterialsData(materialData),
            extra: JSON.stringify({
                referrer: materialItem,
                quality_score: materialData?.qualityScore,
                ...urgencySignalExtra
            }),
            type: 'viewMaterial'
        });
    },
    badges: {
        qualityBadge(materialData: MaterialType) {
            qualityEventsHelpers.track({
                ...qualityEventsHelpers.extractMaterialsData(materialData),
                type: 'quality-badge-tooltip-link-click'
            });
        }
    },
    showMoreMetadata(state: 'expand' | 'collapse') {
        qualityEventsHelpers.track({
            type: `mp_${state}_metadata`,
            extra: 'metadata'
        });
    },
    showMoreButton(extra: string) {
        qualityEventsHelpers.track({
            type: 'show-more-button-click',
            extra
        });
    },
    showInteractiveBundlePreview(material: Material) {
        qualityEventsHelpers.track({
            ...qualityEventsHelpers.extractMaterialsData(material),
            source: 'bundle-list',
            type: 'showInteractiveBundlePreview'
        });
    },
    materialIdCopy(extra: { materialId: number; copiedText: string | undefined }) {
        qualityEventsHelpers.track({
            type: 'material-id-copy',
            extra: JSON.stringify(extra)
        });
    },
    titleCopy(extraInfo: { materialId?: number; copiedText?: string }) {
        qualityEventsHelpers.track({
            type: 'material-title-copy',
            extra: JSON.stringify(extraInfo)
        });
    },
    stickyNavTabClick(section: MaterialPageSectionsNames) {
        qualityEventsHelpers.track({
            type: 'sticky-nav-click',
            extra: section
        });
    },
    learnMoreButton(materialInfo: MaterialType) {
        qualityEventsHelpers.track({
            ...qualityEventsHelpers.extractMaterialsData(materialInfo),
            type: 'click-learn-more-button'
        });
    },
    upsellingModalAddToCart(materials: Material | Material[]) {
        const extractedData = qualityEventsHelpers.extractMaterialsData(materials);

        qualityEventsHelpers.track(
            Array.isArray(extractedData)
                ? extractedData.map((data) => ({ ...data, type: 'upselling-section-add-to-cart' }))
                : { ...extractedData, type: 'upselling-section-add-to-cart' }
        );
    },
    upsellingModalClose() {
        qualityEventsHelpers.track({
            type: 'upselling-modal-close'
        });
    },
    upsellingModalCheckboxToggle(material: Material, checked: boolean) {
        qualityEventsHelpers.track({
            ...qualityEventsHelpers.extractMaterialsData(material),
            type: checked ? 'upselling-modal-checkbox-check' : 'upselling-modal-checkbox-uncheck'
        });
    },
    authorsBlock: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        followUpdate(extra: any) {
            qualityEventsHelpers.track({
                type: 'follow-change-author-block',
                extra: JSON.stringify(extra)
            });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toAuthorPageBtn(extra: any) {
            qualityEventsHelpers.track({
                type: 'to-author-page-button-author-block',
                extra: JSON.stringify(extra)
            });
        }
    },
    starsRatingPopup() {
        qualityEventsHelpers.track({
            type: 'mp_ReviewsWidgetExpand'
        });
    },
    scrollToFeedbacksRatingModal() {
        qualityEventsHelpers.track({
            type: 'mp_clickScrollToReviews'
        });
    },
    sectionAppearance(materialId: number, cache: Set<string>, onFinally: () => void) {
        qualityEventsHelpers
            .track({
                type: 'material-page-sections-appearance',
                extra: JSON.stringify({
                    materialId,
                    sectionIds: Array.from(cache)
                })
            })
            .finally(() => {
                onFinally();
            });
    },
    navigateToAuthor(finallyCallback?: () => void) {
        qualityEventsHelpers
            .track({
                type: 'mp_NavigateToAuthor'
            })
            .finally(finallyCallback);
    },
    interactivePreviewBtnClick() {
        qualityEventsHelpers.track({
            type: 'viewInteractivePreview'
        });
    },
    subjectClassificationClick() {
        qualityEventsHelpers.track({
            type: 'mp_PillsTap',
            extra: 'subject-classification'
        });
    },
    categoriesClick() {
        qualityEventsHelpers.track({
            type: 'mp_PillsTap',
            extra: 'categories'
        });
    },
    followTheAuthor(state: 'follow' | 'unfollow') {
        qualityEventsHelpers.track({
            type: 'mp_FollowTheAuthor',
            extra: state
        });
    },
    feedbacksCommentsTabTap(tab: string | null) {
        qualityEventsHelpers.track({
            type: 'mp_FeedbackCommentsTabTap',
            extra: tab
        });
    },
    feedbackReaction(reactionType?: 'positive' | 'negative') {
        qualityEventsHelpers.track({
            type: reactionType === 'positive' ? 'mp_reviewLike' : 'mp_reviewDislike'
        });
    },
    commentReaction(updatedReactions: Reaction[]) {
        qualityEventsHelpers.track({
            type: 'mp_commentLike',
            extra: JSON.stringify({ updatedReactions })
        });
    },
    addComment(commentText?: string) {
        qualityEventsHelpers.track({
            type: 'comment',
            extra: commentText
        });
    }
};
